import moment from 'moment';
import * as commonConsts from '../pages/common/CommonConsts';
import {
  TranTypeApply,
  TranTypeNotificationChange,
  TranTypeRenew,
  TranTypeAnnualReturn,
  LicTypeInd,
  LicTypeCorp
} from '../utils/Constants';

const getManualAddress = (address) => {
  if (address) {
    return {
      addressLine: address.addressLine,
      suburb: address.suburb,
      state: address.state.value,
      postCode: address.postcode
    };
  }
  return null;
};

const getExistingAddressLine = (addresses, addressType) => {
  let finalAddress;

  const addr = addresses.find((a) => a.addressType === addressType);
  if (addr !== undefined) {
    finalAddress = `${addr.addressLine1}, ${addr.suburb}, ${addr.state}, ${addr.postcode}`;
  }

  return finalAddress;
};

const getDuplicateAddressDetail = (firstAddress, secondAddress) => {
  let address = [];
  if (firstAddress && firstAddress.addressNotChanged === false) {
    if (firstAddress && !firstAddress.international) {
      address = address.concat({
        addressType: firstAddress?.addressType,
        enterManually: firstAddress.enterManually,
        manualAddress: !firstAddress.lookupValue ? getManualAddress(firstAddress) : {},
        lookupValue: firstAddress.lookupValue ? firstAddress.lookupValue : {},
        details: firstAddress.details ? firstAddress.details : {},
        addressNotChanged: firstAddress.addressNotChanged
      });
    } else if (firstAddress && firstAddress.international) {
      const newCountry = secondAddress.country === 'United States of America' ? 'United States' : secondAddress.country;
      address = address.concat({
        addressNotChanged: firstAddress.addressNotChanged,
        addressType: firstAddress?.addressType,
        details: {
          Status: 'Success',
          Address: {
            addressLine1: firstAddress.addressLine,
            suburb: firstAddress.suburb,
            state: firstAddress.state,
            postcode: firstAddress.postcode,
            country: newCountry
          }
        }
      });
    }
  } else if (firstAddress) {
    address = address.concat({
      addressType: firstAddress?.addressType,
      addressNotChanged: firstAddress.addressNotChanged
    });
  }

  if (!secondAddress) {
    return address;
  }

  if (secondAddress.addressNotChanged === false) {
    if (secondAddress && !secondAddress.international) {
      address = address.concat({
        addressType: secondAddress?.addressType,
        enterManually: secondAddress.enterManually,
        manualAddress: !secondAddress.lookupValue ? getManualAddress(secondAddress) : {},
        lookupValue: secondAddress.lookupValue ? secondAddress.lookupValue : {},
        details: secondAddress.details ? secondAddress.details : {}
      });
    } else if (secondAddress && secondAddress.international) {
      const newCountry = secondAddress.country === 'United States of America' ? 'United States' : secondAddress.country;
      address = address.concat({
        addressNotChanged: secondAddress.addressNotChanged,
        addressType: secondAddress?.addressType,
        details: {
          Status: 'Success',
          Address: {
            addressLine1: secondAddress.addressLine,
            suburb: secondAddress.suburb,
            state: secondAddress.state,
            postcode: secondAddress.postcode,
            country: newCountry
          }
        }
      });
    }
  } else {
    address = address.concat({
      addressType: secondAddress?.addressType,
      addressNotChanged: secondAddress.addressNotChanged
    });
  }

  return address;
};

const getNewIndividualTrader = (trader) => {
  return {
    firstName: trader.firstName,
    surName: trader.lastName,
    middleName: trader.middleName,
    contactPart: {
      emailAddress: trader.emailAddress,
      phoneNo:
        trader.primaryContact.prefix && trader.primaryContact.number
          ? `${trader.primaryContact.prefix}-${trader.primaryContact.number}`
          : ''
    },
    addresses: getDuplicateAddressDetail(trader?.businessAddress, trader?.postalAddress),
    roleDetails: [
      {
        RoleType: 'Trader',
        // StartDate: trader.startDate
        StartDate: moment(new Date()).format('YYYY-MM-DD')
      }
    ]
  };
};

const getNewContactPerson = (contact, primaryContact) => {
  return {
    firstName: contact.firstName,
    surName: contact.lastName,
    middleName: contact.middleName,
    contactPart: {
      emailAddress: contact.email,
      phoneNo:
        contact.secondaryContact.prefix && contact.secondaryContact.number
          ? `${contact.secondaryContact.prefix}-${contact.secondaryContact.number}`
          : '',
      // mobileNo: contact.mobile ? `+61-${contact.mobile}` : null
      mobileNo: contact?.mobile
    },
    addresses: getDuplicateAddressDetail(contact?.businessAddress, contact?.postalAddress),
    roleDetails: [
      {
        RoleType: 'Contact Person',
        // StartDate: contact.startDate,
        StartDate: moment(new Date()).format('YYYY-MM-DD'),
        isPrimary: isPrimaryContact(contact, primaryContact)
      }
    ]
  };
};

const getNewOrganisation = (organisation, roleType) => {
  const result = {
    name: organisation.organisationName,
    emailAddress: organisation.organisationEmailAddress,
    phoneNumber:
      organisation.organisationPrimaryContact.prefix && organisation.organisationPrimaryContact.number
        ? `${organisation.organisationPrimaryContact.prefix}-${organisation.organisationPrimaryContact.number}`
        : '',
    addresses: getDuplicateAddressDetail(
      organisation?.organisationBusinessAddress,
      organisation?.organisationPostalAddress
    ),
    abn: organisation?.orgDataInfo?.data?.abn,
    acn: organisation?.orgDataInfo?.data?.acn,
    regoNumber:
      organisation.orgDataInfo.searchCriteria.searchType === commonConsts.OrganisationSearchYincOtherTypeName
        ? organisation.registrationNumber
        : null,
    roleDetails: [
      {
        RoleType: roleType,
        // StartDate: organisation.startDate
        StartDate: moment(new Date()).format('YYYY-MM-DD')
      }
    ],
    roles: []
  };

  return result;
};

const getMultipleAddress = (address, primaryAddress, addressType) => {
  if (primaryAddress && primaryAddress.addressNotChanged === false) {
    if (primaryAddress && !primaryAddress.international) {
      address = address.concat({
        addressType: addressType,
        enterManually: primaryAddress.enterManually,
        manualAddress: !primaryAddress.lookupValue ? getManualAddress(primaryAddress) : {},
        lookupValue: primaryAddress.lookupValue ? primaryAddress.lookupValue : {},
        details: primaryAddress.details ? primaryAddress.details : {},
        addressNotChanged: primaryAddress.addressNotChanged
      });
    } else if (primaryAddress && primaryAddress.international) {
      address = address.concat({
        addressNotChanged: primaryAddress.addressNotChanged,
        addressType: addressType,
        details: {
          Status: 'Success',
          Address: {
            addressLine1: primaryAddress.addressLine,
            suburb: primaryAddress.suburb,
            state: primaryAddress.state,
            postcode: primaryAddress.postcode,
            country: primaryAddress.country
          }
        }
      });
    }
  } else if (primaryAddress) {
    address = address.concat({
      addressType: addressType,
      addressNotChanged: primaryAddress.addressNotChanged
    });
  }

  return address;
};

const getMultipleAddressDetail = (primaryAddress, otherAddresses, sameAsRegisteredBusiness) => {
  let address = [];
  address = getMultipleAddress(address, primaryAddress, 'Registered Office');

  if (sameAsRegisteredBusiness[0]) {
    address = getMultipleAddress(address, primaryAddress, 'Business');
  } else {
    if (otherAddresses.length > 0) {
      address = getMultipleAddress(address, otherAddresses[0], 'Business');
    }
  }

  if (sameAsRegisteredBusiness[1]) {
    address = getMultipleAddress(address, primaryAddress, 'Postal');
  } else {
    if (otherAddresses.length > 1) {
      address = getMultipleAddress(address, otherAddresses[1], 'Postal');
    }
  }

  return address;
};

const isPrimaryContact = (contact, primaryContactName) => {
  const contactName = contact.middleName
    ? `${contact.firstName} ${contact.middleName} ${contact.lastName}`
    : `${contact.firstName} ${contact.lastName}`;

  return contactName === primaryContactName;
};

const getNewIndividualAccountant = (accountant, relatedOrgName) => {
  return {
    firstName: accountant?.firstName,
    surname: accountant?.lastName,
    middleName: accountant?.middleName,
    contactPart: {
      emailAddress: accountant?.emailAddress,
      phoneNo: `${accountant?.primaryContactNumber?.prefix}-${accountant?.primaryContactNumber?.number}`
    },
    // startDate: accountant?.startDate,
    addresses: getDuplicateAddressDetail(accountant?.businessAddress, accountant?.postalAddress),
    roleDetails: [
      {
        RoleType: 'Auditor',
        StartDate: moment(new Date()).format('DD/MM/YYYY')
      }
    ],
    qualifications: [
      {
        certificateNumber: accountant?.qualification?.certificateNumber,
        certificateName: accountant?.qualification?.qualifications,
        details: accountant?.qualification?.details
      }
    ],
    relatedOrgName: relatedOrgName
  };
};

const getNewOrganisationAccountant = (accountant) => {
  return {
    id: accountant?.siebelRowId ? accountant?.siebelRowId : null,
    integrationId: accountant?.integrationId ? accountant?.integrationId : null,
    name: accountant.businessName,
    addresses: getDuplicateAddressDetail(accountant?.businessAddress, accountant?.postalAddress),
    abn: accountant.organisationData?.abn,
    acn: accountant.organisationData?.acn,
    regoNumber:
      accountant.organisationSearch?.manualYinc === true
        ? accountant?.registrationNumber
        : accountant?.organisationData?.yincOther,
    roleDetails: [
      {
        RoleType: 'Auditor',
        StartDate: moment(new Date()).format('DD/MM/YYYY')
      }
    ],
    roles: [],
    emailAddress: accountant?.organisationEmailAddress,
    phoneNumber:
      accountant?.organisationPrimaryContact?.prefix && accountant?.organisationPrimaryContact?.number
        ? `${accountant?.organisationPrimaryContact?.prefix}-${accountant?.organisationPrimaryContact?.number}`
        : ''
  };
};

const getNewLeadAuditor = (accountant) => {
  const leadAuditor = accountant?.organisationDetails;

  return {
    firstName: leadAuditor?.contactFirstName,
    surname: leadAuditor?.contactLastName,
    middleName: leadAuditor?.contactMiddleName,
    contactPart: {
      emailAddress: leadAuditor?.contactEmailAddress,
      phoneNo: `${leadAuditor?.contactPrimaryContactNumber.prefix}-${leadAuditor?.contactPrimaryContactNumber.number}`
    },
    addresses: getDuplicateAddressDetail(leadAuditor?.contactBusinessAddress, leadAuditor?.contactPostalAddress),
    qualifications: [
      {
        certificateNumber: leadAuditor?.contactQualification.certificateNumber,
        certificateName: leadAuditor?.contactQualification.qualifications,
        details: leadAuditor?.contactQualification.details
      }
    ],
    roleDetails: [
      {
        RoleType: 'Auditor',
        StartDate: moment(new Date()).format('DD/MM/YYYY')
      }
    ],
    roles: [],
    relatedOrgName: accountant?.businessName
  };
};

const getIndexOfValueInArray = (value, arr, prop) => {
  for (var i = 0; i < arr.length; i++) {
    if (arr[i][prop] === value) {
      return i;
    }
  }
  return -1; //to handle the case where the value doesn't exist
};

const getPremiseAddressLine = (address) => {
  let finalAddress;

  if (address !== undefined) {
    finalAddress = `${address.addressLine1}, ${address.suburb}, ${address.state}, ${address.postcode}`;
  } else {
    finalAddress = '';
  }

  return finalAddress;
};

const getDuplicateAddressDetailWthOutAddressNotChanged = (firstAddress, secondAddress) => {
  let address = [];
  if (firstAddress?.lookupValue || firstAddress?.enterManually == true || firstAddress?.addressLine) {
    if (firstAddress && !firstAddress.international) {
      address = address.concat({
        addressType: firstAddress?.addressType,
        enterManually: firstAddress.enterManually,
        manualAddress: !firstAddress.lookupValue ? getManualAddress(firstAddress) : {},
        lookupValue: firstAddress.lookupValue ? firstAddress.lookupValue : {},
        details: firstAddress.details ? firstAddress.details : {},
        addressNotChanged: firstAddress.addressNotChanged
      });
    } else if (firstAddress && firstAddress.international) {
      address = address.concat({
        addressNotChanged: firstAddress.addressNotChanged,
        addressType: firstAddress?.addressType,
        details: {
          Status: 'Success',
          Address: {
            addressLine1: firstAddress.addressLine,
            suburb: firstAddress.suburb,
            state: firstAddress.state,
            postcode: firstAddress.postcode,
            country: firstAddress.country
          }
        }
      });
    }
  }

  if (!(secondAddress?.lookupValue || secondAddress?.enterManually == true || secondAddress?.addressLine)) {
    return address;
  }

  if (secondAddress && !secondAddress.international) {
    address = address.concat({
      addressType: secondAddress?.addressType,
      enterManually: secondAddress.enterManually,
      manualAddress: !secondAddress.lookupValue ? getManualAddress(secondAddress) : {},
      lookupValue: secondAddress.lookupValue ? secondAddress.lookupValue : {},
      details: secondAddress.details ? secondAddress.details : {}
    });
  } else if (secondAddress && secondAddress.international) {
    address = address.concat({
      addressNotChanged: secondAddress.addressNotChanged,
      addressType: secondAddress?.addressType,
      details: {
        Status: 'Success',
        Address: {
          addressLine1: secondAddress.addressLine,
          suburb: secondAddress.suburb,
          state: secondAddress.state,
          postcode: secondAddress.postcode,
          country: secondAddress.country
        }
      }
    });
  } else {
    address = address.concat({
      addressType: secondAddress?.addressType,
      addressNotChanged: secondAddress.addressNotChanged
    });
  }

  return address;
};

const getAddresUrlPath = () => {
  const transactionType = getAddressTransType();
  const licType = getAddressLicType();
  return `${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_ADDRESS_SEARCH_PREFIX}/api/${transactionType}/${licType}`;
};

const getAddressTransType = () => {
  const currentPath = window.location.pathname;
  if (currentPath.includes('new-application')) {
    return TranTypeApply;
  }

  if (currentPath.includes('notification-change')) {
    return TranTypeNotificationChange;
  }

  if (currentPath.includes('annual-return')) {
    return TranTypeAnnualReturn;
  }

  if (currentPath.includes('renewal')) {
    return TranTypeRenew;
  }
};

const getAddressLicType = () => {
  const currentPath = window.location.pathname;
  if (currentPath.includes('individual')) {
    return LicTypeInd;
  }

  if (currentPath.includes('organisation')) {
    return LicTypeCorp;
  }

  return 'CHARITABLE';
};

export {
  getNewIndividualTrader,
  getDuplicateAddressDetail,
  getNewOrganisation,
  getMultipleAddressDetail,
  getNewContactPerson,
  isPrimaryContact,
  getExistingAddressLine,
  getNewIndividualAccountant,
  getNewOrganisationAccountant,
  getNewLeadAuditor,
  getIndexOfValueInArray,
  getPremiseAddressLine,
  getDuplicateAddressDetailWthOutAddressNotChanged,
  getAddresUrlPath,
  getAddressTransType,
  getAddressLicType
};
